<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header
        variant="buttons"
        :saving="loading"
        @cancel="closeModal()"
        @submit="save"
      >
        <h1>{{ t(`${tKey}.title`) }}</h1>
      </modal-header>
    </template>

    <form @submit="save">
      <div class="mt-12">
        <div class="flex flex-col w-full">
          <form-card
            :title="t('general')"
            :description="t(tKey + '.description')"
          >
            <div class="flex flex-col gap-y-5">
              <Multiselect
                v-model="rawVisibilityBicycle.region"
                :error="errors?.region"
                :options="regions"
                :loading="loadingRegions"
                :label="t('pages.visibility.bicycle.region')"
                class="w-full"
                :can-deselect="false"
                :can-clear="false"
                :searchable="true"
                @select="setMapCenterAndUpdate()"
              />

              <Multiselect
                v-model="rawVisibilityBicycle.gpsTrackerId"
                :error="errors?.gpsTrackerId"
                :options="trackers"
                :loading="loadingTrackers"
                :disabled="loadingTrackers"
                :label="t('pages.visibility.bicycle.freeTrackers')"
                class="w-full"
                :can-deselect="true"
                :can-clear="true"
                :searchable="true"
                @select="setMapCenterAndUpdate()"
                @clear="clearTracker()"
              />

              <LocationMap ref="map" v-model="marker" v-model:radius="radius" />
              <div class="flex gap-x-5">
                <FormInput
                  :model-value="marker?.lat"
                  :error="errors?.gisLocation?.lat"
                  disabled
                  class="w-1/2"
                  type="text"
                  :label="t('lat')"
                  pattern="^\d*\.?\d*$"
                />
                <FormInput
                  :model-value="marker?.long"
                  :error="errors?.gisLocation?.long"
                  disabled
                  class="w-1/2"
                  type="text"
                  :label="t('long')"
                  pattern="^\d*\.?\d*$"
                />
              </div>
              <FormInput
                v-model="rawVisibilityBicycle.name"
                :error="errors?.name"
                class="col-start-1 col-end-2"
                type="text"
                :label="t('pages.visibility.bicycle.name')"
                :placeholder="t('pages.visibility.bicycle.name')"
              />

              <Multiselect
                v-model="rawVisibilityBicycle.status"
                :error="errors?.status"
                :options="fetchStatus"
                :label="t('pages.visibility.bicycle.status')"
                class="w-full"
                :can-deselect="false"
                :can-clear="false"
                :searchable="true"
              />
              <FormInput
                v-model="rawVisibilityBicycle.number"
                :error="errors?.number"
                class="col-start-1 col-end-2"
                type="number"
                :label="t('pages.visibility.bicycle.number')"
                :placeholder="t('pages.visibility.bicycle.number')"
              />
              <FormInput
                v-model="rawVisibilityBicycle.gpsTrackerNumber"
                disabled
                :error="errors?.gpsTrackerNumber"
                class="col-start-1 col-end-2"
                type="number"
                :label="t('pages.visibility.bicycle.gpsTrackerNumber')"
              />
              <FormInput
                v-model="rawVisibilityBicycle.boxNumber"
                :error="errors?.boxNumber"
                class="col-start-1 col-end-2"
                type="number"
                :label="t('pages.visibility.bicycle.boxNumber')"
                :placeholder="t('pages.visibility.bicycle.boxNumber')"
              />
              <FormInput
                v-model="rawVisibilityBicycle.brand"
                :error="errors?.brand"
                class="col-start-1 col-end-2"
                type="text"
                :label="t('pages.visibility.bicycle.brand')"
                :placeholder="t('pages.visibility.bicycle.brand')"
              />
              <FormInput
                v-model="rawVisibilityBicycle.color"
                :error="errors?.brand"
                class="col-start-1 col-end-2"
                type="text"
                :label="t('pages.visibility.bicycle.color')"
                :placeholder="t('pages.visibility.bicycle.color')"
              />
              <FormInput
                v-model="rawVisibilityBicycle.comment"
                :error="errors?.comment"
                class="col-start-1 col-end-2"
                type="text"
                :label="t('pages.visibility.bicycle.comment')"
                :placeholder="t('pages.visibility.bicycle.comment')"
              />
            </div>
          </form-card>
        </div>
      </div>
    </form>
  </modal>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import { number, object, string } from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import Multiselect from '@utils/Multiselect.vue';
import FormInput from '@utils/inputs/Input.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import { syncRef } from '@hooks/state';
import LocationMap from '@utils/maps/PickLocationRadiusMap.vue';
import { enumOptionsProvider, useProviderOpt } from '@utils/table/hooks/provider';
import FormCard from '@utils/FormCard.vue';
import {
  type EnrichedVisibilityBicycleDto,
  type Region,
  type VisibilityBicycle,
  VisibilityBicycleStatus,
  type VisibilityTracker,
  type VisibilityTrackerOption,
} from '@/entities/Entities';

interface Props {
  open: boolean;
  visibilityBicycle?: EnrichedVisibilityBicycleDto;
}

const emit = defineEmits<{(e: 'update:open', value: boolean): void
  (e: 'update:visibilityLocation', value: VisibilityBicycle): void
  (e: 'saved'): void
}>();
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const internalVisibilityBicycle = useVModel(props, 'visibilityBicycle', emit);

const isNew = computed(() => !props.visibilityBicycle);
const tKey = computed(() =>
  (isNew.value ? 'pages.visibility.bicycle.modal.new' : 'pages.visibility.bicycle.modal.edit'));

const { t } = useI18n();

const notify = useNotify();

const initialData = (visibilityBicycle?: VisibilityBicycle) => (visibilityBicycle
  ? { ...visibilityBicycle }
  : {
    name: '',
    gisLocation: undefined,
    region: undefined,
    brand: '',
    status: VisibilityBicycleStatus.ACTIVE,
    number: 0,
    boxNumber: 0,
    color: '',
    comment: '',
    gpsTrackerId: null,
    gpsTrackerNumber: null,
  });

const {
  data: regions,
  loading: loadingRegions,
} = useProviderOpt<Region>(
  '/regions',
  {
    sort: {
      sortKey: 'name',
      sortDirection: 'ASC',
    },
  },
  (value: Region) => ({
    label: value.name,
    value: value['@id'],
    gisLocation: value.gisLocation,
  }),
);

const {
  data: trackers,
  loading: loadingTrackers,
  refetch: fetchTrackers,
} = useProviderOpt<VisibilityTracker, VisibilityTrackerOption>(
  '/visibility_trackers',
  {},
  (value) => {
    const location = value.latestPoints?.[0]?.location;
    return {
      label: `${value.serial} Letzte Pos.: ${value.latestPoints?.[0]?.address}`,
      value: Number(value.id) ?? null,
      tracker: value,
      ...(location?.lat !== undefined && location?.lng !== undefined
        ? {
          gisLocation: {
            lat: location.lat,
            long: location.lng,
          },
        }
        : {}),
    };
  },
  {
    immediate: true,
  },
);

const loadTrackers = async () => {
  // eslint-disable-next-line no-use-before-define
  const currentTrackerId = Number(rawVisibilityBicycle?.value?.gpsTrackerId ?? NaN);
  await fetchTrackers();
  trackers.value = trackers.value.filter(
    (tracker) =>
      !tracker.tracker?.inUse || tracker.tracker.id === currentTrackerId,
  );
};

watch(() => internalOpen.value, () => {
  if (internalOpen.value) {
    loadTrackers();
  }
});

const fetchStatus = enumOptionsProvider('/enums/VisibilityBicycleStatus', (item) => ({
  value: item,
  label: t(`pages.visibility.bicycle.visibilityBicycleStatus.${item}`),
}));

const rawVisibilityBicycle = syncRef(internalVisibilityBicycle, (visibilityBicycle: EnrichedVisibilityBicycleDto) => ({
  ...initialData(visibilityBicycle?.visibilityBicycle),
  region: visibilityBicycle?.visibilityBicycle?.region?.['@id'],
  gpsTrackerId: visibilityBicycle?.visibilityBicycle?.gpsTrackerId || null,
  gpsTrackerNumber: visibilityBicycle?.visibilityBicycle?.gpsTrackerNumber || null,
}));

const {
  data: visibilityBicycle,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    name: string()
      .nullable(),
    region: string()
      .nullable()
      .required(t('validation.notEmpty')),
    gpsTrackerId: number()
      .nullable(),
    gpsTrackerNumber: number()
      .nullable(),
    status: string()
      .nullable()
      .required(t('validation.notEmpty')),
    number: number()
      .nullable()
      .required(t('validation.notEmpty')),
    boxNumber: number()
      .nullable(),
    brand: string()
      .nullable(),
    color: string()
      .nullable(),
    comment: string()
      .nullable(),
  }),
  rawVisibilityBicycle,
);

function resetData() {
  rawVisibilityBicycle.value = { ...initialData() } as unknown as typeof visibilityBicycle['value'];
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
  internalVisibilityBicycle.value = undefined;
}

const map = ref();
const radius = ref(20);
const marker = ref({
  lat: 0,
  long: 0,
});

const setMapCenterAndUpdate = () => {
  const trackerOptTmp = trackers.value.find((tracker) => tracker?.value === rawVisibilityBicycle.value?.gpsTrackerId);
  const serial = Number(trackerOptTmp?.tracker?.serial);
  rawVisibilityBicycle.value.gpsTrackerNumber = Number.isNaN(serial) ? null : serial;

  const loc = trackerOptTmp?.gisLocation;
  if (loc) {
    marker.value.lat = loc?.lat ?? 0;
    marker.value.long = loc?.long ?? 0;
    map.value?.moveToCenter(loc?.lat, loc?.long);
  }
};

const clearTracker = () => {
  rawVisibilityBicycle.value.gpsTrackerId = null;
  rawVisibilityBicycle.value.gpsTrackerNumber = null;
};

const [exec, { loading }] = useFetchAction();

const save = validatedFn(async () => {
  try {
    const edit = !!props.visibilityBicycle?.visibilityBicycle;
    const url = edit ? props.visibilityBicycle?.visibilityBicycle['@id'] : '/visibility_bicycles';

    await exec(url, {
      method: edit ? 'PUT' : 'POST',
      body: {
        name: visibilityBicycle.value.name,
        region: visibilityBicycle.value.region,
        gpsTrackerId: visibilityBicycle.value.gpsTrackerId,
        gpsTrackerNumber: visibilityBicycle.value.gpsTrackerNumber,
        status: visibilityBicycle.value.status,
        number: visibilityBicycle.value.number,
        boxNumber: visibilityBicycle.value.boxNumber,
        brand: visibilityBicycle.value.brand,
        color: visibilityBicycle.value.color,
        comment: visibilityBicycle.value.comment,
      },
    });

    notify?.({
      variant: 'success',
      title: t(`${tKey.value}.saveSuccess`),
    });

    emit('saved');
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t(`${tKey.value}.saveError`),
    });
  }
});
</script>

<style lang="scss" scoped></style>
